import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2, Mail, Check, ArrowLeft, ArrowRight,
  Building2, User as UserIcon, Search, Phone, MessageSquare,
  CheckCircle2,
} from "lucide-react";
import { OplyxLogo } from "@/components/brand/OplyxLogo";
import { LiveNode } from "@/components/brand/Flow";
import { BRAND } from "@/brand";
import PaymentSetup from "@/components/billing/PaymentSetup";

// ──────────────────────────── helpers ────────────────────────────
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const formatCnpj = (s) => {
  const d = onlyDigits(s).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};
const formatPhoneBR = (s) => {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{0,2})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) =>
    [a && `(${a}${a.length === 2 ? ")" : ""}`, b && ` ${b}`, c && `-${c}`].filter(Boolean).join("")
  );
  return d.replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3");
};
const cnpjLooksValid = (s) => onlyDigits(s).length === 14;
// Mesma regra de legal_rep_lib.cpf_is_valid (dígitos verificadores).
const cpfValid = (s) => {
  const d = onlyDigits(s);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const n of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < n; i++) sum += Number(d[i]) * (n + 1 - i);
    if (((sum * 10) % 11) % 10 !== Number(d[n])) return false;
  }
  return true;
};
const formatCpf = (s) => {
  const d = onlyDigits(s).slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, "$1.$2").replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
};
// CPF da Receita vem mascarado ("***112108**"): confere só os dígitos visíveis.
const cpfMatchesMasked = (cpf, masked) => {
  const d = onlyDigits(cpf);
  return d.length === 11 && (masked || "").length === 11
    && [...masked].every((c, i) => !/\d/.test(c) || c === d[i]);
};
// Indicação vinda da assinatura "Gestão por OPLYX" (ver Landing.jsx): chega na
// URL (landing em outro domínio) ou no localStorage (mesmo domínio).
const indicacao = () => {
  const p = new URLSearchParams(window.location.search);
  let ref = p.get("ref");
  let src = p.get("ref_src");
  if (!ref) {
    try {
      const r = JSON.parse(localStorage.getItem("oplyx_ref") || "null");
      if (r && Date.now() - r.at < 60 * 24 * 3600 * 1000) { ref = r.ref; src = r.src; }
    } catch { /* sem storage */ }
  }
  return ref ? { ref, ref_src: src || undefined } : {};
};
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || "");
const initialSignup = {
  // step 1 — plan
  selected_plan: "",
  // step 2 — empresa
  business_cnpj: "",
  business_razao_social: "",
  business_name: "",
  phone: "",
  whatsapp_contact: "",
  // step 3 — conta
  name: "",
  email: "",
  password: "",
  // quem responde pela empresa (quem cadastra vira administrador da conta)
  signer_cpf: "",
  signer_is_partner: null,
  legal_rep: { name: "", email: "", cpf: "", qualificacao: "" },
  qsa: [],
  // endereço vindo da consulta de CNPJ (a página de cartão do Asaas exige)
  address: null,
  // aceite dos Termos de Uso e ciência da Política de Privacidade
  accepted_terms: false,
};

// ─────────────────────────── component ───────────────────────────
export default function AuthPage() {
  const [mode, setMode] = useState("login");
  // signup wizard state
  const [step, setStep] = useState(1);
  const [signup, setSignup] = useState(initialSignup);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [cnpjLookupLoading, setCnpjLookupLoading] = useState(false);
  const [signupDone, setSignupDone] = useState(false); // conta criada → passo 4 (pagamento)

  // login state (kept separate to avoid leaking between modes)
  const [login, setLogin] = useState({ email: "", password: "" });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const nav = useNavigate();

  // Landing CTA → ?mode=signup&email=... pré-preenche
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "signup") setMode("signup");
    const email = params.get("email");
    const plan = params.get("plan");
    if (email) {
      setLogin((f) => ({ ...f, email }));
      setSignup((f) => ({ ...f, email }));
    }
    if (plan) setSignup((f) => ({ ...f, selected_plan: plan }));
  }, []);

  // Load active plans whenever the signup wizard becomes visible
  useEffect(() => {
    if (mode !== "signup" || plans.length) return;
    setPlansLoading(true);
    api.get("/public/plans")
      .then((r) => {
        setPlans(r.data || []);
        // preselect first if URL didn't pick one
        setSignup((f) => ({ ...f, selected_plan: f.selected_plan || (r.data?.[0]?.key || "basic") }));
      })
      .catch(() => toast.error("Não foi possível carregar os planos. Tente novamente."))
      .finally(() => setPlansLoading(false));
  }, [mode, plans.length]);

  // ── CNPJ enrichment ────────────────────────────────────────────
  const lookupCnpj = async () => {
    if (!cnpjLooksValid(signup.business_cnpj)) {
      toast.error("Digite os 14 dígitos do CNPJ para consultar a Receita.");
      return;
    }
    setCnpjLookupLoading(true);
    try {
      const { data } = await api.get(`/public/cnpj/${onlyDigits(signup.business_cnpj)}`);
      setSignup((f) => ({
        ...f,
        business_razao_social: data.razao_social || f.business_razao_social,
        business_name: data.nome_fantasia || data.razao_social || f.business_name,
        phone: f.phone || (data.telefone ? formatPhoneBR(data.telefone) : ""),
        qsa: Array.isArray(data.qsa) ? data.qsa : [],
        address: {
          cep: data.cep || "", logradouro: data.logradouro || "", numero: data.numero || "",
          complemento: data.complemento || "", bairro: data.bairro || "",
          municipio: data.municipio || "", uf: data.uf || "",
        },
      }));
      toast.success("Dados da empresa carregados da Receita Federal.");
    } catch (e) {
      const det = e?.response?.data?.detail || "Não foi possível consultar a Receita.";
      toast.error(det);
    } finally {
      setCnpjLookupLoading(false);
    }
  };

  // ── Submitters ────────────────────────────────────────────────
  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", login);
      localStorage.setItem("oplyx_token", data.token);
      localStorage.setItem("oplyx_user", JSON.stringify(data.user));
      if (data.user?.active_branch_id) localStorage.setItem("oplyx_branch", data.user.active_branch_id);
      else localStorage.removeItem("oplyx_branch");
      toast.success("Bem-vindo de volta!");
      nav(data.user?.is_platform_admin ? "/admin" : "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciais inválidas");
    } finally { setLoading(false); }
  };

  const submitSignup = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      const payload = {
        name: signup.name.trim(),
        email: signup.email.trim().toLowerCase(),
        password: signup.password,
        business_name: signup.business_name.trim(),
        business_cnpj: onlyDigits(signup.business_cnpj),
        business_razao_social: signup.business_razao_social.trim(),
        phone: signup.phone.trim(),
        whatsapp_contact: signup.whatsapp_contact.trim(),
        selected_plan: signup.selected_plan || "basic",
        source: "landing",
        accepted_terms: signup.accepted_terms,
        address: signup.address,
        signer_cpf: onlyDigits(signup.signer_cpf),
        signer_is_partner: signup.signer_is_partner,
        ...(signup.signer_is_partner === false ? {
          legal_rep: {
            name: signup.legal_rep.name.trim(),
            email: signup.legal_rep.email.trim().toLowerCase(),
            cpf: onlyDigits(signup.legal_rep.cpf),
            qualificacao: signup.legal_rep.qualificacao,
          },
        } : {}),
        ...indicacao(),
      };
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem("oplyx_token", data.token);
      localStorage.setItem("oplyx_user", JSON.stringify(data.user));
      if (data.user?.active_branch_id) localStorage.setItem("oplyx_branch", data.user.active_branch_id);
      setSignupDone(true);
      setStep(4);
      toast.success("Conta criada. Falta só a forma de pagamento.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao criar conta";
      setSubmitError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  // ── Step navigation validation ────────────────────────────────
  const step1Valid = !!signup.selected_plan;
  const step2Valid =
    cnpjLooksValid(signup.business_cnpj) &&
    signup.business_razao_social.trim().length >= 2 &&
    signup.business_name.trim().length >= 2 &&
    onlyDigits(signup.phone).length >= 10 &&
    onlyDigits(signup.whatsapp_contact).length >= 10;
  const step3Valid =
    signup.accepted_terms &&
    signup.name.trim().length >= 2 &&
    isEmail(signup.email) &&
    signup.password.length >= 6 &&
    cpfValid(signup.signer_cpf) &&
    signup.signer_is_partner !== null &&
    (signup.signer_is_partner ||
      (signup.legal_rep.name.trim().length >= 3 && isEmail(signup.legal_rep.email) &&
        (!onlyDigits(signup.legal_rep.cpf) || cpfValid(signup.legal_rep.cpf))));

  const goNext = () => {
    if (step === 1 && !step1Valid) return toast.error("Escolha um plano para continuar.");
    if (step === 2 && !step2Valid) return toast.error("Confira CNPJ, nome do negócio, telefone e WhatsApp.");
    setStep((s) => Math.min(3, s + 1));
  };

  // ─────────────────── render ─────────────────────────────────
  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="auth-page">
      {/* LEFT — brand panel */}
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#0B0D0F] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute left-12 top-1/2 right-12 h-px bg-gradient-to-r from-transparent via-[#00FF88]/30 to-transparent" />
        <div className="relative">
          <OplyxLogo size={28} tone="dark" className="text-xl tracking-tight" />
        </div>
        <div className="relative space-y-6">
          <h1 className="text-5xl font-semibold leading-[1.05] tracking-tight">
            A camada operacional<br />do seu negócio.
          </h1>
          <p className="text-neutral-400 text-base max-w-md leading-relaxed">{BRAND.taglinePt}</p>
          <div className="flex items-center gap-2 text-xs text-neutral-500 pt-2">
            <LiveNode size={6} />
            <span>Sistema ativo: operação em tempo real</span>
          </div>
        </div>
        <div className="relative text-xs text-neutral-600 tracking-wide uppercase">{BRAND.tagline}</div>
      </div>

      {/* RIGHT — auth panel */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white dark:bg-[#0B0D0F]">
        <Card className="w-full max-w-md border-0 shadow-none md:shadow-sm dark:bg-transparent">
          <CardContent className="p-6 md:p-8">

            {/* Header — switches title per mode */}
            <div className="mb-6">
              <div className="md:hidden mb-6"><OplyxLogo size={22} tone="adaptive" /></div>
              {mode === "login" && (
                <>
                  <h2 className="text-3xl font-semibold tracking-tight">Entrar</h2>
                  <p className="text-muted-foreground text-sm mt-1">Acesse seu painel.</p>
                </>
              )}
              {mode === "signup" && !signupDone && (
                <>
                  <h2 className="text-3xl font-semibold tracking-tight">Criar conta</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Sem fidelidade e sem multa. Cancelamento com aviso de 30 dias.
                  </p>
                  <SignupStepper step={step} />
                </>
              )}
              {mode === "signup" && signupDone && (
                <>
                  <div className="w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight">Forma de pagamento</h2>
                  <p className="text-muted-foreground text-sm mt-1">
                    Último passo: cadastre como prefere pagar para liberar o acesso.
                  </p>
                  <SignupStepper step={4} />
                </>
              )}
            </div>

            {/* LOGIN form */}
            {mode === "login" && (
              <form onSubmit={submitLogin} className="space-y-4" data-testid="login-form">
                <div>
                  <Label>E-mail</Label>
                  <Input data-testid="auth-email" type="email" required
                    value={login.email}
                    onChange={(e) => setLogin({ ...login, email: e.target.value })} />
                </div>
                <div>
                  <Label>Senha</Label>
                  <Input data-testid="auth-password" type="password" required minLength={6}
                    value={login.password}
                    onChange={(e) => setLogin({ ...login, password: e.target.value })} />
                </div>
                <Button data-testid="auth-submit" type="submit" className="w-full h-11 rounded-full" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Entrar
                </Button>
                <button type="button"
                  onClick={() => { setForgotEmail(login.email || ""); setForgotSent(false); setForgotOpen(true); }}
                  className="text-xs text-emerald-700 hover:underline w-full text-center"
                  data-testid="forgot-password-link"
                >Esqueci minha senha</button>
              </form>
            )}

            {/* SIGNUP wizard */}
            {mode === "signup" && !signupDone && (
              <div data-testid="signup-wizard">
                {step === 1 && (
                  <StepPlans
                    plans={plans}
                    loading={plansLoading}
                    value={signup.selected_plan}
                    onChange={(k) => setSignup((f) => ({ ...f, selected_plan: k }))}
                  />
                )}
                {step === 2 && (
                  <StepEmpresa
                    form={signup}
                    setForm={setSignup}
                    onLookup={lookupCnpj}
                    lookupLoading={cnpjLookupLoading}
                  />
                )}
                {step === 3 && (
                  <>
                    <StepConta form={signup} setForm={setSignup} />
                    <label className="mt-4 flex items-start gap-3 text-sm cursor-pointer select-none">
                      <input type="checkbox" checked={signup.accepted_terms}
                        onChange={(e) => setSignup({ ...signup, accepted_terms: e.target.checked })}
                        className="mt-0.5 shrink-0" data-testid="signup-terms" />
                      <span>
                        Li e aceito os{" "}
                        <a href="/termos" target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">
                          Termos de Uso e Contrato de Licença
                        </a>{" "}
                        (com os{" "}
                        <a href="/dpa" target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">Anexos I</a>{" "}e{" "}
                        <a href="/ia" target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">II</a>)
                        {" "}e li a{" "}
                        <a href="/privacidade" target="_blank" rel="noreferrer" className="underline text-emerald-700 font-medium">
                          Política de Privacidade
                        </a>{" "}
                        da OPLYX. Declaro que contrato em nome da empresa do CNPJ informado.
                      </span>
                    </label>
                  </>
                )}

                {submitError && step === 3 && (
                  <div className="mt-4 p-3 rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950/30 text-sm text-rose-700 dark:text-rose-300" data-testid="signup-error">
                    {submitError}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-6">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    disabled={step === 1 || loading}
                    data-testid="signup-back"
                    className="rounded-full"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                  </Button>
                  {step < 3 ? (
                    <Button
                      type="button"
                      onClick={goNext}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="signup-next"
                    >
                      Continuar <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={submitSignup}
                      disabled={loading || !step3Valid}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="signup-submit"
                    >
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Continuar para o pagamento
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* SIGNUP passo 4: forma de pagamento */}
            {mode === "signup" && signupDone && (
              <PaymentSetup initialAddress={signup.address} onActivated={() => nav("/")} />
            )}

            {!signupDone && (
              <button
                onClick={() => { setMode(mode === "login" ? "signup" : "login"); setStep(1); }}
                className="mt-6 text-sm text-muted-foreground hover:text-foreground w-full text-center"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Não tem conta? Criar uma agora" : "Já tem conta? Entrar"}
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Forgot-password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader><DialogTitle>Recuperar senha</DialogTitle></DialogHeader>
          {!forgotSent ? (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setForgotLoading(true);
                try {
                  await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
                  setForgotSent(true);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Erro ao solicitar recuperação");
                } finally { setForgotLoading(false); }
              }}
              className="space-y-3"
            >
              <p className="text-sm text-muted-foreground">Digite seu e-mail cadastrado. Enviaremos uma nova senha temporária.</p>
              <div>
                <Label>E-mail</Label>
                <Input type="email" required autoFocus value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)} data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={forgotLoading} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="forgot-submit-btn">
                {forgotLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Mail className="w-4 h-4 mr-1" />Enviar nova senha
              </Button>
            </form>
          ) : (
            <div className="space-y-3 text-center py-2" data-testid="forgot-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto grid place-items-center">
                <Mail className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-sm text-foreground font-medium">Tudo certo!</p>
              <p className="text-sm text-muted-foreground">Se este e-mail estiver cadastrado, uma nova senha temporária foi enviada para a sua caixa de entrada. Verifique também o spam.</p>
              <Button onClick={() => setForgotOpen(false)} className="w-full rounded-full" variant="outline">Voltar ao login</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────── Stepper ───────────────────────
function SignupStepper({ step }) {
  const items = ["Plano", "Empresa", "Conta", "Pagamento"];
  return (
    <div className="flex items-center gap-2 mt-5" data-testid="signup-stepper">
      {items.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div
              className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold transition-colors ${
                done ? "bg-emerald-600 text-white"
                : active ? "bg-foreground text-background"
                : "bg-muted text-muted-foreground"
              }`}
              data-testid={`stepper-dot-${n}`}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : n}
            </div>
            <span className={`text-xs ${active ? "text-foreground font-medium" : "text-muted-foreground"}`}>{label}</span>
            {i < items.length - 1 && <div className="flex-1 h-px bg-muted" />}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────── Step 1 — Plans ───────────────────────
// Curated highlights por plano — escolhi 5-6 que vendem a diferença,
// não o features dict inteiro (poluição visual).
const PLAN_HIGHLIGHTS = {
  basic: [
    "PDV + Caixa + Comandas",
    "NFC-e e NF-e ilimitadas (até 500 docs/mês)",
    "Estoque com Custo Médio Ponderado",
    "Financeiro (contas a pagar/receber)",
    "Compras + Fornecedores",
    "Dashboards e relatórios de vendas",
  ],
  full: [
    "Tudo do Basic +",
    "Multi-filial (até 9.999 unidades)",
    "CRM + Cashback + Lista de espera",
    "Receitas, Ficha técnica e DRE",
    "Ponto eletrônico + Banco de Horas",
    "Reposição automática + KDS + Metas",
    "Webhooks e API pública",
  ],
};

function StepPlans({ plans, loading, value, onChange }) {
  if (loading) return (
    <div className="py-10 text-center text-sm text-muted-foreground" data-testid="plans-loading">
      <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Carregando planos…
    </div>
  );
  if (!plans.length) return (
    <p className="text-sm text-muted-foreground py-6">Nenhum plano disponível no momento.</p>
  );
  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
  return (
    <div className="space-y-3" data-testid="step-plans">
      <p className="text-sm text-muted-foreground">Escolha o plano que melhor se adapta ao seu negócio. Sem fidelidade e sem multa de cancelamento. Você pode pedir o encerramento a qualquer momento, com aviso prévio de 30 dias. Durante esse período, o serviço e as mensalidades continuam normalmente. Antes de confirmar, mostramos as datas de encerramento, as cobranças restantes e o prazo para exportar seus dados.</p>
      {plans.map((p) => {
        const selected = value === p.key;
        const highlights = PLAN_HIGHLIGHTS[p.key] || [];
        return (
          <button
            type="button"
            key={p.key}
            onClick={() => onChange(p.key)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
              selected ? "border-emerald-600 bg-emerald-50/60 dark:bg-emerald-950/30" : "border-border hover:border-foreground/20"
            }`}
            data-testid={`plan-card-${p.key}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-base truncate">{p.name || p.key}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {p.doc_limit_month ? `${p.doc_limit_month.toLocaleString("pt-BR")} docs fiscais/mês` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-semibold tabular-nums">{fmt(p.month)}<span className="text-xs text-muted-foreground">/mês</span></div>
              </div>
            </div>

            {highlights.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid={`plan-highlights-${p.key}`}>
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${selected ? "text-emerald-600" : "text-foreground/50"}`} />
                    <span className={i === 0 && h.endsWith("+") ? "font-medium text-foreground/80" : ""}>{h}</span>
                  </li>
                ))}
              </ul>
            )}

            {selected && (
              <div className="mt-3 flex items-center gap-1 text-xs text-emerald-700 font-medium border-t border-emerald-200/50 pt-2">
                <Check className="w-3.5 h-3.5" /> Plano selecionado
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────── Step 2 — Empresa ───────────────────────
function StepEmpresa({ form, setForm, onLookup, lookupLoading }) {
  return (
    <div className="space-y-3" data-testid="step-empresa">
      <p className="text-sm text-muted-foreground">Conte sobre sua empresa. Esses dados serão usados para emissão fiscal e contato.</p>
      <p className="text-xs text-muted-foreground" data-testid="signup-pj-only">A OPLYX é contratada só por empresas, com CNPJ ativo. Não há contratação como pessoa física.</p>

      <div>
        <Label>CNPJ</Label>
        <div className="flex gap-2">
          <Input
            data-testid="signup-cnpj"
            value={form.business_cnpj}
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            onChange={(e) => setForm((f) => ({ ...f, business_cnpj: formatCnpj(e.target.value) }))}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!cnpjLooksValid(form.business_cnpj) || lookupLoading}
            onClick={onLookup}
            className="rounded-full shrink-0"
            data-testid="signup-cnpj-lookup"
          >
            {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">Consultar</span>
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">Consultamos a Receita Federal para preencher Razão Social e Nome Fantasia.</p>
      </div>

      <div>
        <Label className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Razão Social</Label>
        <Input data-testid="signup-razao-social" value={form.business_razao_social}
          onChange={(e) => setForm((f) => ({ ...f, business_razao_social: e.target.value }))}
          placeholder="Ex: Café da Ana LTDA" />
      </div>

      <div>
        <Label>Nome Fantasia</Label>
        <Input data-testid="signup-business" value={form.business_name}
          onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
          placeholder="Ex: Café da Ana" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Telefone</Label>
          <Input data-testid="signup-phone" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: formatPhoneBR(e.target.value) }))}
            placeholder="(11) 99999-0000" inputMode="tel" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> WhatsApp</Label>
          <Input data-testid="signup-whatsapp" value={form.whatsapp_contact}
            onChange={(e) => setForm((f) => ({ ...f, whatsapp_contact: formatPhoneBR(e.target.value) }))}
            placeholder="(11) 99999-0000" inputMode="tel" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────── Step 3 — Conta ───────────────────────
function StepConta({ form, setForm }) {
  return (
    <div className="space-y-3" data-testid="step-conta">
      <p className="text-sm text-muted-foreground">Crie suas credenciais de acesso. Esse usuário será o administrador do seu negócio.</p>
      <div>
        <Label className="flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" /> Seu nome</Label>
        <Input data-testid="signup-name" required value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div>
        <Label>E-mail</Label>
        <Input data-testid="signup-email" type="email" required value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
      </div>
      <div>
        <Label>Senha</Label>
        <Input data-testid="signup-password" type="password" required minLength={6} value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
        <p className="text-[11px] text-muted-foreground mt-1">Mínimo de 6 caracteres.</p>
      </div>
      <StepResponsavel form={form} setForm={setForm} />
    </div>
  );
}

// Quem responde pela empresa no contrato social. Quem cadastra vira
// administrador da conta, e isso nem sempre é o sócio (legal_rep_lib).
function StepResponsavel({ form, setForm }) {
  const qsa = form.qsa || [];
  const representantes = qsa.filter(s => s.tipo === "pf" && s.pode_representar);
  const cpfOk = cpfValid(form.signer_cpf);
  const souNaReceita = cpfOk && qsa.some(s => s.tipo === "pf" && cpfMatchesMasked(form.signer_cpf, s.documento_mascarado));
  const setRep = (patch) => setForm(f => ({ ...f, legal_rep: { ...f.legal_rep, ...patch } }));
  const opt = (value, label) => (
    <button type="button" key={label} onClick={() => setForm(f => ({ ...f, signer_is_partner: value }))}
      data-testid={`signup-partner-${value ? "sim" : "nao"}`}
      className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${form.signer_is_partner === value ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-medium" : "border-neutral-200 hover:border-neutral-300"}`}>
      {label}
    </button>
  );
  return (
    <div className="space-y-3 border-t pt-3">
      <div>
        <Label>Seu CPF</Label>
        <Input data-testid="signup-cpf" inputMode="numeric" value={form.signer_cpf} placeholder="000.000.000-00"
          onChange={(e) => setForm((f) => ({ ...f, signer_cpf: formatCpf(e.target.value) }))} />
        {onlyDigits(form.signer_cpf).length === 11 && !cpfOk && (
          <p className="text-[11px] text-rose-600 mt-1">CPF inválido. Confira os números.</p>
        )}
      </div>
      <div>
        <Label>Você é sócio(a) ou administrador(a) da empresa no contrato social?</Label>
        <div className="flex gap-2 mt-1">{opt(true, "Sim")}{opt(false, "Não")}</div>
        {form.signer_is_partner === true && cpfOk && qsa.length > 0 && !souNaReceita && (
          <p className="text-[11px] text-amber-700 mt-1">
            Seu CPF não aparece no quadro de sócios da Receita para este CNPJ. Pode seguir; a OPLYX confere depois com você.
          </p>
        )}
      </div>
      {form.signer_is_partner === false && (
        <div className="space-y-2 rounded-md bg-neutral-50 p-3">
          <p className="text-xs text-muted-foreground">
            Informe quem responde pela empresa. Ele não precisa ter acesso ao sistema. A OPLYX vai pedir que ele confirme o contrato.
          </p>
          {representantes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {representantes.map(s => (
                <button type="button" key={s.nome + s.documento_mascarado}
                  onClick={() => setRep({ name: s.nome, qualificacao: s.qualificacao })}
                  className={`rounded-full border px-3 py-1 text-xs ${form.legal_rep.name === s.nome ? "border-emerald-500 bg-emerald-50" : "border-neutral-200 bg-white"}`}>
                  {s.nome} <span className="text-muted-foreground">({s.qualificacao})</span>
                </button>
              ))}
            </div>
          )}
          <div>
            <Label>Nome completo</Label>
            <Input data-testid="signup-rep-name" value={form.legal_rep.name} onChange={(e) => setRep({ name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>E-mail dele(a)</Label>
              <Input data-testid="signup-rep-email" type="email" value={form.legal_rep.email} onChange={(e) => setRep({ email: e.target.value })} />
            </div>
            <div>
              <Label>CPF dele(a) (opcional)</Label>
              <Input data-testid="signup-rep-cpf" inputMode="numeric" value={form.legal_rep.cpf}
                onChange={(e) => setRep({ cpf: formatCpf(e.target.value) })} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
